import React from "react";
import { cn, DEAL_LABELS, type DealRating } from "~/lib/utils";

/**
 * Atomic component for the categorical Deal Rating.
 *
 * Square (radius 2px), not pill — looks more data-ops than SaaS.
 * Leading colored square dot is the only visual marker; color is reinforced
 * by text so we're WCAG-clean.
 */
export function DealChip({
  rating,
  size = "md",
  className,
}: {
  rating: DealRating;
  size?: "sm" | "md";
  className?: string;
}): React.JSX.Element {
  const dotBg: Record<DealRating, string> = {
    super: "bg-deal-super",
    good: "bg-deal-good",
    fair: "bg-deal-fair",
    high: "bg-deal-high",
    over: "bg-deal-over",
    unknown: "bg-ink-disabled",
  };
  const softBg: Record<DealRating, string> = {
    super: "bg-deal-super-soft text-deal-super",
    good: "bg-deal-good-soft text-deal-good",
    fair: "bg-deal-fair-soft text-deal-fair",
    high: "bg-deal-high-soft text-deal-high",
    over: "bg-deal-over-soft text-deal-over",
    unknown: "bg-surface-sunken text-ink-tertiary",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 border font-medium tracking-[0.01em]",
        size === "sm" ? "h-5 px-1.5 text-[10.5px] rounded-[2px]" : "h-6 px-2 text-[11.5px] rounded-[2px]",
        softBg[rating],
        "border-current/15",
        className,
      )}
      data-deal={rating}
    >
      <span className={cn("inline-block", dotBg[rating], size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2 rounded-[1px]")} />
      <span>{DEAL_LABELS[rating]}</span>
    </span>
  );
}
